import {
  Alert,
  Collapse,
  Group,
  Loader,
  Table,
  Text,
  UnstyledButton,
} from '@mantine/core';
import { IconChevronDown, IconChevronRight } from '@tabler/icons-react';
import {
  useGetPreviousClaimBatchDetails,
  useListPreviousClaimBatches,
} from 'api/resource/claim';
import { useSettingsStore } from 'store/SettingsStore';
import { Fragment, useMemo, useState } from 'react';
import { formatDateOnly } from 'utils/dateUtils';

const formatBatchDate = (isoValue: string | null | undefined) => {
  const formatted = formatDateOnly(isoValue);
  return formatted === 'N/A' ? '-' : formatted;
};

const formatTariffs = (
  tariffs: Array<{
    code: string;
    units?: number | null;
  }>
) => {
  if (!tariffs.length) return '-';
  return tariffs
    .map((tariff) =>
      tariff.units && tariff.units > 1
        ? `${tariff.code} x${tariff.units}`
        : tariff.code
    )
    .join(', ');
};

const BatchDetails = ({
  siteID,
  batchID,
  opened,
}: {
  siteID: string;
  batchID: string;
  opened: boolean;
}) => {
  const { data, isLoading, isError } = useGetPreviousClaimBatchDetails({
    siteID,
    batchID,
    enabled: opened,
  });

  if (!opened) return null;

  if (isLoading) {
    return (
      <Group p="sm">
        <Loader size="xs" />
        <Text size="sm">Loading batch details...</Text>
      </Group>
    );
  }

  if (isError || !data) {
    return (
      <Alert color="red" variant="light" m="sm">
        Unable to load previous batches. Please try again.
      </Alert>
    );
  }

  if (!data.claims.length) {
    return (
      <Text size="sm" c="dimmed" p="sm">
        No claims found for this batch.
      </Text>
    );
  }

  return (
    <Table striped highlightOnHover withTableBorder withColumnBorders m="sm">
      <Table.Thead>
        <Table.Tr>
          <Table.Th>Patient</Table.Th>
          <Table.Th>Date of Service</Table.Th>
          <Table.Th>Claim Number</Table.Th>
          <Table.Th>Tariffs Applied</Table.Th>
        </Table.Tr>
      </Table.Thead>
      <Table.Tbody>
        {data.claims.map((claim) => (
          <Table.Tr key={claim.id}>
            <Table.Td>{claim.patientName}</Table.Td>
            <Table.Td>{formatBatchDate(claim.dateOfService)}</Table.Td>
            <Table.Td>{claim.claimNumber || '-'}</Table.Td>
            <Table.Td>{formatTariffs(claim.tariffs)}</Table.Td>
          </Table.Tr>
        ))}
      </Table.Tbody>
    </Table>
  );
};

export const MyPreviousBatches = () => {
  const [openedBatchId, setOpenedBatchId] = useState<string | null>(null);
  const currentSiteID = useSettingsStore((state) => state.defaultSiteID);

  const { data, isLoading, isError } = useListPreviousClaimBatches({
    siteID: currentSiteID || '',
    page: 1,
    pageSize: 25,
  });

  const batches = useMemo(() => data?.batches || [], [data?.batches]);

  return (
    <>
      {isLoading ? (
        <Group>
          <Loader size="sm" />
          <Text size="sm">Loading batches...</Text>
        </Group>
      ) : null}

      {isError ? (
        <Alert color="red" variant="light">
          Unable to load previous batches. Please try again.
        </Alert>
      ) : null}

      {!isLoading && !isError && batches.length === 0 ? (
        <Text c="dimmed">
          No batches found. Batches you create will appear here.
        </Text>
      ) : null}

      {!isLoading && !isError && batches.length > 0 ? (
        <Table striped highlightOnHover withTableBorder withColumnBorders>
          <Table.Thead>
            <Table.Tr>
              <Table.Th />
              <Table.Th>Created Date</Table.Th>
              <Table.Th>Provider</Table.Th>
              <Table.Th>Claim Count</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {batches.map((batch) => {
              const opened = openedBatchId === batch.batchID;
              return (
                <Fragment key={batch.batchID}>
                  <Table.Tr>
                    <Table.Td>
                      <UnstyledButton
                        onClick={() =>
                          setOpenedBatchId(opened ? null : batch.batchID)
                        }
                        aria-label={`toggle-batch-${batch.batchID}`}
                      >
                        {opened ? (
                          <IconChevronDown size={16} />
                        ) : (
                          <IconChevronRight size={16} />
                        )}
                      </UnstyledButton>
                    </Table.Td>
                    <Table.Td>{formatBatchDate(batch.createdAt)}</Table.Td>
                    <Table.Td>{batch.provider.displayName || '-'}</Table.Td>
                    <Table.Td>{batch.claimCount}</Table.Td>
                  </Table.Tr>
                  <Table.Tr>
                    <Table.Td colSpan={4} p={0}>
                      <Collapse in={opened}>
                        <BatchDetails
                          siteID={currentSiteID || ''}
                          batchID={batch.batchID}
                          opened={opened}
                        />
                      </Collapse>
                    </Table.Td>
                  </Table.Tr>
                </Fragment>
              );
            })}
          </Table.Tbody>
        </Table>
      ) : null}
    </>
  );
};
